import { memo, useContext, useMemo } from 'react';
import Color from 'color';
import { getDay, isWeekend } from 'date-fns';
import styled from '@emotion/styled';
import type { Chat, KolProfileMessage  } from '@trinity/chat';
import { ChatCitationType } from '@trinity/chat';
import {
  ClickCitationContext,
  MessageCitationsToggleContext,
} from '@trinity/chat-components/components/Chat/context';
import { ChatResponsePane } from '@trinity/chat-components/components/Chat/ResponsePane';
import { MessageCitations } from '@trinity/chat-components/components/Chat.Message.Citations/index';
import { StyledSup } from '@trinity/chat-components/components/Markdown/MarkdownElements';
import { colors } from '@trinity/chat-components/components/Theme/theme';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context';
import { ProfileAccordionContainer } from './KolSearch.Profile.Accordion';
import { AITooltip } from './KolSearch.Profile.AITooltip';

const Day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type Props = {
  citations: Chat.Citation[];
  profile: KolProfileMessage;
};

export const AvailabilityInsights = memo((props: Props) => {
  const openCitation = useContext(ClickCitationContext);
  const interactions = props.citations.find(
    (x) => x.id === String(ChatCitationType.FbmsInteractions),
  );

  const hasSnippets = !!interactions?.snippets.length;

  return (
    <ProfileItem>
      <ProfileAccordionContainer
        profileKey={`HCP Availability Insights`}
        renderChildren={() => (
          <AvailabilityRoot>
            {hasSnippets && (
              <AvailabilityInsightsTable
                citations={props.citations}
                profile={props.profile} />
            )}
            {!hasSnippets && (
              <AvailabilityParagraph>
                {`This HCP has no previous MSL interactions to draw availability insights from.`}
              </AvailabilityParagraph>
            )}
          </AvailabilityRoot>
        )}
      >
        <Label>
          {`HCP Availability Insights`}
          {hasSnippets && (
            <StyledSup
              onClick={() => openCitation(interactions.ordinal.toString())}
            >{`${interactions.snippets.length} ${interactions.snippets.length === 1 ? 'Interaction' : 'Interactions'}`}</StyledSup>
          )}
          <InfoTooltip />
        </Label>
      </ProfileAccordionContainer>
    </ProfileItem>
  );
});

export const AvailabilityInsightsMessage = memo((props: Props) => {
  const interactions = props.citations.find(
    (x) => x.id === String(ChatCitationType.FbmsInteractions),
  );
  const count = interactions?.snippets?.length || 0;
  const [citationsOpen, toggleCitationsOpen] = useContext(
    MessageCitationsToggleContext,
  );

  return (
    <>
      <AvailabilityParagraph>{`Sure! I can assist you with scheduling a time with ${props.profile.name}.`}</AvailabilityParagraph>
      <Label>
        {`HCP Availability Insights`}
        {!!count && (
          <StyledSup
            onClick={toggleCitationsOpen}
          >{`${count} citations`}</StyledSup>
        )}
        <InfoTooltip />
      </Label>
      <div>
        <AvailabilityInsightsTable
          citations={props.citations}
          profile={props.profile}
        />
        <StyledCitationsPane
          title={`Sources (${count})`}
          open={citationsOpen}
          toggleOpen={toggleCitationsOpen}
        >
          <StyledMessageCitations items={[interactions]} />
        </StyledCitationsPane>
      </div>
    </>
  );
});

export const AvailabilityInsightsTable = memo((props: Props) => {
  const color = useColors();

  let items: {
    frequency: number;
    label: string;
    color: string;
  }[];
  let interactionsCount: number;
  let disclaimer = `Availability data is based on past interactions and historical engagement patterns. These insights serve as a guide and may not be precise.`;

  if (props.profile.availabilityInsights) {
    const {
      totalInteractions,
      insights,
      disclaimer: computedDisclaimer,
    } = props.profile.availabilityInsights;
    interactionsCount = totalInteractions;
    disclaimer = computedDisclaimer || disclaimer;

    const colorMap = {
      HIGH: color.Dark,
      MEDIUM: color.Medium,
      LOW: color.Light,
      ZERO: color.Lighter,
    };

    // We only care about the first 5 days of the week (Mon-Fri)
    const weekdays = Object.keys(insights).slice(1, 6);
    items = weekdays.map((day, i) => {
      const { pct, rank } = insights[day];
      const dayIndex = parseInt(day); // Convert to 0-based index

      return {
        frequency: pct * 100,
        label: Day[dayIndex - 1],
        color: colorMap[rank],
      };
    });
  } else {
    // Fallback to the old way of calculating availability insights on the client side
    const interactions = props.citations.find(
      (x) => x.id === String(ChatCitationType.FbmsInteractions),
    ) as Chat.Citation<ChatCitationType.FbmsInteractions>;

    const occurrences = (interactions?.snippets || []).reduce(
      (acc, x) => {
        const date = new Date(x.metadata.createdOn);

        if (isWeekend(date)) return acc;

        const index = getDay(date);

        acc[index - 1] += 1;

        return acc;
      },
      [0, 0, 0, 0, 0],
    );

    const sum = occurrences.reduce((acc, x) => acc + x, 0);
    const frequencies =
      sum > 0 ? occurrences.map((item) => (item * 100) / sum) : occurrences;
    const rankings = getRankedArray(frequencies);

    function getRankColor(i: number) {
      const item = rankings.find((x) => x.index === i);

      if (item.rank === Rank.Zero) return color.Lighter;

      if (item.rank === Rank.High) return color.Dark;
      if (item.rank === Rank.Medium) return color.Medium;
      return color.Light;
    }
    items = frequencies.map((x, i) => ({
      frequency: x,
      label: Day[i + 1], // Skip Sunday
      color: getRankColor(i),
    }));

    interactionsCount = interactions?.snippets?.length || 0;
  }

  return (
    <>
      <AvailabilityParagraph>{`These predictions are based on ${interactionsCount} interactions.`}</AvailabilityParagraph>
      <AvailabilityTextNotes>
        <AvailabilityTextNote>{disclaimer}</AvailabilityTextNote>
      </AvailabilityTextNotes>
      <WeekdayRow>
        <WeekdayRowTitle>Day</WeekdayRowTitle>
        <WeekdayCells>
          {items.map((x) => (
            <AvailabilityWeekday
              frequency={x.frequency}
              key={x.label}
              label={x.label}
              color={x.color}
            />
          ))}
        </WeekdayCells>
      </WeekdayRow>
    </>
  );
});

const InfoTooltip = memo(() => {
  const aiGenerated = `
**Physical Access Guide**

This section provides insights on the best days and time slots for meeting the HCP based on past interaction data.

* **% Values:** How often meetings happened on each day and time slot.
* **Color Guide:** Darker = more past engagements.
* **Plan Smart:** Pick the highest % values for better scheduling.
`;

  return <AITooltip helpText={aiGenerated}>{`i`}</AITooltip>;
});

function getRankedArray(values: number[]) {
  const arr = values.map((x, i) => ({ value: x, index: i }));
  const max = Math.max(...values);
  const min = Math.min(...values.filter((x) => x > 0));
  return arr.map((x) => {
    let rank: typeof Rank[keyof typeof Rank];
    if (x.value === max) {
      rank = Rank.High;
    } else if (x.value > min) {
      rank = Rank.Medium;
    } else if (x.value === min) {
      rank = Rank.Low;
    } else {
      rank = Rank.Zero;
    }
    return {
      value: x.value,
      rank,
      index: x.index,
    };
  });
}

type AvailabilityWeekdayProps = {
  frequency?: number;
  label: string;
  color?: string;
};

const AvailabilityWeekday = ({
  frequency = 0,
  color,
  label,
}: AvailabilityWeekdayProps) => {
  return (
    <WeekdayCell style={{ backgroundColor: color }}>
      <WeekdayLabel>{label}</WeekdayLabel>
      <WeekdayTextSub>{`(${Math.trunc(frequency)} %)`}</WeekdayTextSub>
    </WeekdayCell>
  );
};

const AvailabilityRoot = styled.div({
  paddingLeft: 30,
});

const WeekdayRow = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 5,
});

const WeekdayCells = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 5,
});

const WeekdayRowTitle = styled.div({
  fontFamily: 'var(--font-bold)',
});

const WeekdayCell = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  width: 95,
  height: 40,
  backgroundColor: '#f3f3f3',
  borderRadius: 10,
  fontSize: 15,
});

const WeekdayTextSub = styled.div({
  fontSize: 10,
});

const WeekdayLabel = styled.div({
  fontSize: 13,
});

const AvailabilityParagraph = styled.div({
  marginBottom: 15,
});

const AvailabilityTextNote = styled.div({
  fontStyle: 'italic',
});

const AvailabilityTextNotes = styled.div({
  marginBottom: 15,
});

const ProfileItem = styled.div({
  marginBottom: 12,
});

const Label = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.bold,
  display: 'flex',
  alignItems: 'center',
  gap: 5,
}));

const useColors = () => {
  const { palette, query } = useContext(AppThemingContext);

  return useMemo(() => {
    if (!query.data?.enabled) {
      return {
        Lighter: '#f3f3f3',
        Light: Color(colors.blue.main).alpha(0.25).rgb().string(),
        Medium: Color(colors.blue.main).alpha(0.4).rgb().string(),
        Dark: Color(colors.blue.main).alpha(0.5).rgb().string(),
      };
    }

    return {
      Lighter: '#f3f3f3',
      Light: Color(palette.primary.main).alpha(0.25).rgb().string(),
      Medium: Color(palette.primary.main).alpha(0.4).rgb().string(),
      Dark: Color(palette.primary.main).alpha(0.5).rgb().string(),
    };
  }, [query.data?.enabled, palette]);
};

const Rank = {
  High: 1,
  Medium: 2,
  Low: 3,
  Zero: 0,
};

const StyledCitationsPane = styled(ChatResponsePane)`
  margin-top: 15px;
`;

const StyledMessageCitations = styled(MessageCitations)`
  margin-top: 10px;
`;
